import { SignedIn, SignedOut, UserButton, useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import useIsMobile from '../hooks/useIsMobile';

export default function Landing() {
  const navigate = useNavigate();
  const { getToken, isSignedIn } = useAuth();
  const isMobile = useIsMobile();
  const [planInfo, setPlanInfo] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    async function fetchPlan() {
      if (!isSignedIn) return;
      try {
        const token = await getToken();
        const res = await axios.get(
          process.env.REACT_APP_API_URL + '/api/payments/my-plan',
          { headers: { Authorization: 'Bearer ' + token } }
        );
        setPlanInfo(res.data);
      } catch (err) {
        console.log('Plan fetch failed:', err.message);
      }
    }
    fetchPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn]);

  const handleAnalyzeClick = async () => {
    if (!isSignedIn) {
      navigate('/sign-up');
      return;
    }
    try {
      const token = await getToken();
      const res = await axios.get(
        process.env.REACT_APP_API_URL + '/api/payments/my-plan',
        { headers: { Authorization: 'Bearer ' + token } }
      );
      const plan = res.data;
      if (
        plan &&
        plan.free_analysis_used &&
        plan.resume_analyses_remaining <= 0 &&
        plan.interview_analyses_remaining <= 0
      ) {
        sessionStorage.setItem('showUpgradeModal', 'true');
        navigate('/analyze');
      } else {
        navigate('/analyze');
      }
    } catch (err) {
      navigate('/analyze');
    }
  };

  // Helper to compute display count correctly
  function resumeCount(p) {
    if (!p) return null;
    if (!p.free_analysis_used) {
      return p.resume_analyses_remaining > 0
        ? p.resume_analyses_remaining + 1
        : '1 free';
    }
    return p.resume_analyses_remaining;
  }

  function interviewCount(p) {
    if (!p) return null;
    if (!p.free_analysis_used) {
      return p.interview_analyses_remaining > 0
        ? p.interview_analyses_remaining + 1
        : '1 free';
    }
    return p.interview_analyses_remaining;
  }

  function resumeColor(p) {
    if (!p) return '#00D4AA';
    if (!p.free_analysis_used) return '#00D4AA';
    return p.resume_analyses_remaining > 0 ? '#00D4AA' : '#ff6b6b';
  }

  function interviewColor(p) {
    if (!p) return '#5b8cff';
    if (!p.free_analysis_used) return '#5b8cff';
    return p.interview_analyses_remaining > 0 ? '#5b8cff' : '#ff6b6b';
  }

  const pillStyle = {
    background: '#141B2D',
    border: '1px solid #1a2640',
    borderRadius: '6px',
    padding: '0.3rem 0.75rem',
    fontSize: '0.75rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
  };

  const plans = [
    {
      name: 'Free', price: '₹0', tag: null,
      desc: '1 complete analysis',
      features: ['ATS Score', 'Resume Improvements', 'Interview Prep', '1 analysis saved'],
      planType: null, color: '#1a2640',
    },
    {
      name: 'ResumePlus', price: '₹99', tag: null,
      desc: '3 analyses',
      features: ['ATS Score', 'Full Resume Improvements', 'Last 3 analyses saved'],
      planType: 'resume_plus', color: '#1a2640',
    },
    {
      name: 'ResumePro', price: '₹179', tag: null,
      desc: '7 analyses',
      features: ['ATS Score', 'Full Resume Improvements', 'Last 7 analyses saved'],
      planType: 'resume_pro', color: '#1a2640',
    },
    {
      name: 'InterviewPlus', price: '₹249', tag: null,
      desc: '3 analyses',
      features: ['ATS Score', 'Full Interview Prep', 'Last 3 analyses saved'],
      planType: 'interview_plus', color: '#1a2640',
    },
    {
      name: 'InterviewPro', price: '₹499', tag: null,
      desc: '7 analyses',
      features: ['ATS Score', 'Full Interview Prep', 'Last 7 analyses saved'],
      planType: 'interview_pro', color: '#1a2640',
    },
    {
      name: 'Elite', price: '₹699', tag: 'BEST VALUE',
      desc: '7 analyses — everything',
      features: ['ATS Score', 'Full Resume Improvements', 'Full Interview Prep', 'Last 15 analyses saved'],
      planType: 'elite', color: '#00D4AA',
    },
  ];

  const testimonials = [
    {
      name: 'Abhishek Singh',
      role: 'Cracked BCG Associate role',
      prev: 'Previously: Manger, FMCG',
      text: 'CrackPrep tool is great at telling you the kinks in your resume, which is essential in this ATS era. The interview prep guide is something I have not witnessed before. Too good...',
      score: 78,
    },
    {
      name: 'Mahika Khare',
      role: 'BSA at Google',
      prev: 'Previously: Lead Business Analyst, Meesho',
      text: 'The interview prep section generated product cases and cutlure fit questions that were almost identical to what Google actually asked me. The CV questions were tailored to on my actual resume, not generic. I feel like the ₹499 plan is a great bargain.',
      score: 82,
    },
    {
      name: 'Madhulika Saxena',
      role: 'Analyst, Accenture',
      prev: 'Previously: Fresher',
      text: 'Most ATS tools just tell you to add keywords. CrackPrep actually helps rewrite the entire experience section to match the target company\'s JD. The HR questions seem particularly prepared keeping the target job profile in consideration.',
      score: 79,
    },
  ];

  const faqs = [
    {
      q: 'Is my first analysis really free?',
      a: 'Yes — completely free, no credit card required. Your first analysis includes ATS score, full resume improvements, and complete interview prep. No hidden charges.',
    },
    {
      q: 'How is CrackPrep different from other ATS tools?',
      a: 'Most ATS tools just match keywords. CrackPrep reads your entire resume, understands your career story, and generates interview questions specific to your target company — not generic templates. A BCG application gets case studies. A Flipkart PM role gets product cases. The prep is as specific as having a personal coach.',
    },
    {
      q: 'How long does an analysis take?',
      a: 'Typically 5-7 minutes. The program runs three sequential analyses — ATS scoring, resume improvement, and interview prep generation — all tailored to your specific resume and target JD.',
    },
    {
      q: 'What file formats are supported?',
      a: 'PDF and DOCX for your resume. For job descriptions you can paste the text directly or upload a PDF, DOCX, or TXT file.',
    },
    {
      q: 'Can I use the same plan for multiple companies?',
      a: 'Yes — each analysis is a fresh run against a different JD. If you have 3 analyses on ResumePlus, you can use them for 3 different target companies.',
    },
    {
      q: 'Are my resume and data kept private?',
      a: 'Yes. Your resume text is stored securely and only used to generate your analysis. We never share your data with third parties for marketing. See our Privacy Policy for full details.',
    },
  ];

  return (
    <div style={{ background: '#0A0F1E', minHeight: '100vh', color: 'white', fontFamily: 'Arial, sans-serif', overflowX: 'hidden' }}>

      {/* ── NAVBAR ── */}
      <nav style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: isMobile ? '0.75rem 1rem' : '1rem 2rem',
        borderBottom: '1px solid #1a2640',
        position: 'sticky', top: 0, background: '#0A0F1E', zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '1rem' : '2rem' }}>
          <span onClick={() => navigate('/')}
            style={{ color: '#00D4AA', fontWeight: 'bold', fontSize: isMobile ? '1.2rem' : '1.5rem', cursor: 'pointer' }}>
            CrackPrep
          </span>
          {!isMobile && (
            <>
              <button onClick={() => navigate('/pricing')}
                style={{ background: 'transparent', color: '#8899aa', border: 'none', cursor: 'pointer', fontSize: '0.95rem' }}>
                Pricing
              </button>
              <button onClick={() => navigate('/about')}
                style={{ background: 'transparent', color: '#8899aa', border: 'none', cursor: 'pointer', fontSize: '0.95rem' }}>
                About Us
              </button>
            </>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <SignedOut>
            {!isMobile && (
              <button onClick={() => navigate('/sign-in')}
                style={{ background: 'transparent', color: '#00D4AA', border: '1px solid #00D4AA', padding: '0.5rem 1.2rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem' }}>
                Sign In
              </button>
            )}
            <button onClick={() => navigate('/sign-up')}
              style={{ background: '#00D4AA', color: '#0A0F1E', border: 'none', padding: '0.5rem 1rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: isMobile ? '0.85rem' : '0.9rem' }}>
              Sign Up Free
            </button>
          </SignedOut>
            <SignedIn>
  {/* Desktop counters */}
  {planInfo && !isMobile && (
    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
      <div style={pillStyle}>
        <span style={{ color: '#00D4AA' }}>📝</span>
        <span style={{ color: '#8899aa' }}>Resume:</span>
        <span style={{ color: resumeColor(planInfo), fontWeight: 'bold' }}>
          {resumeCount(planInfo)}
        </span>
      </div>
      <div style={pillStyle}>
        <span style={{ color: '#5b8cff' }}>🎯</span>
        <span style={{ color: '#8899aa' }}>Interview:</span>
        <span style={{ color: interviewColor(planInfo), fontWeight: 'bold' }}>
          {interviewCount(planInfo)}
        </span>
      </div>
    </div>
  )}

  {/* Mobile counters */}
  {planInfo && isMobile && (
    <div style={{ fontSize: '0.65rem', color: '#8899aa', lineHeight: 1.4, textAlign: 'right' }}>
      <div><span style={{ color: '#00D4AA' }}>R:</span> <span style={{ color: resumeColor(planInfo) }}>{resumeCount(planInfo)}</span></div>
      <div><span style={{ color: '#5b8cff' }}>I:</span> <span style={{ color: interviewColor(planInfo) }}>{interviewCount(planInfo)}</span></div>
    </div>
  )}

  {/* History button — visible on both mobile and desktop */}
  <button onClick={() => navigate('/history')}
    style={{ background: 'transparent', color: '#00D4AA', border: '1px solid #00D4AA', padding: isMobile ? '0.4rem 0.6rem' : '0.5rem 1rem', borderRadius: '6px', cursor: 'pointer', fontSize: isMobile ? '0.75rem' : '0.85rem' }}>
    {isMobile ? '📋' : 'My Analyses'}
  </button>

  <UserButton afterSignOutUrl="/" />
</SignedIn>
        </div>
      </nav>

      {/* ── HERO ── */}
      <div style={{ textAlign: 'center', padding: isMobile ? '3rem 1.5rem 2rem' : '5rem 2rem 3rem', position: 'relative' }}>
        <div style={{ position: 'absolute', top: '30%', left: '50%', transform: 'translate(-50%, -50%)', width: '600px', height: '300px', background: 'radial-gradient(ellipse, rgba(0,212,170,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <h1 style={{ fontSize: isMobile ? '2.2rem' : '3.8rem', fontWeight: 'bold', marginBottom: '1rem', lineHeight: 1.15 }}>
          Your resume keeps getting rejected?<br />
          <span style={{ color: '#00D4AA' }}>Got an interview but unsure about how to prepare.</span>
        </h1>

        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: '#141B2D', border: '1px solid #1a2640', borderRadius: '20px', padding: '0.4rem 1rem', marginBottom: '1.5rem', fontSize: '0.8rem', color: '#8899aa' }}>
          <span style={{ color: '#00D4AA' }}>★</span>
          Built by experts from IIT/IIM/ISB based on successful resumes and interview Q&As
        </div>

        <p style={{ fontSize: isMobile ? '1rem' : '1.2rem', color: '#8899aa', maxWidth: '620px', margin: '0 auto 1rem', lineHeight: 1.8 }}>
          85% of resumes are filtered out by ATS before reaching a recruiter.
          CrackPrep fixes your resume, boosts your ATS score, and prepares you
          for every question your target company will ask — in under 5 minutes.
        </p>

        <p style={{ fontSize: '0.95rem', color: 'white', fontWeight: 'bold', marginBottom: '0.5rem' }}>
          Our USP is our Interview Prep Guide. Try it once and you won't be disappointed.
        </p>

        <p style={{ fontSize: '0.9rem', color: '#8899aa', marginBottom: '2rem' }}>
          First analysis completely free. No credit card required.
        </p>

        <button onClick={handleAnalyzeClick}
          style={{ background: '#00D4AA', color: '#0A0F1E', border: 'none', padding: isMobile ? '0.875rem 2rem' : '1rem 3rem', borderRadius: '8px', fontSize: isMobile ? '1rem' : '1.15rem', fontWeight: 'bold', cursor: 'pointer', width: isMobile ? '100%' : 'auto', maxWidth: '360px', boxShadow: '0 0 30px rgba(0,212,170,0.3)' }}>
          Analyze My Resume Free →
        </button>

        <p style={{ color: '#8899aa', fontSize: '0.8rem', marginTop: '0.75rem' }}>
          ✓ Just signup to see your ATS score &nbsp;·&nbsp; ✓ Results in under 5 minutes
        </p>
      </div>

      {/* ── SOCIAL PROOF BAR ── */}
      <div style={{ borderTop: '1px solid #1a2640', borderBottom: '1px solid #1a2640', padding: isMobile ? '1.5rem 1rem' : '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: isMobile ? '1.5rem' : '4rem', flexWrap: 'wrap', maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
          {[
            { number: '300+', label: 'Job profiles studied' },
            { number: '200+', label: 'Successful candidates' },
            { number: '50+',  label: 'Top companies covered' },
            { number: '4 min', label: 'Average analysis time' },
          ].map(stat => (
            <div key={stat.label}>
              <div style={{ fontSize: isMobile ? '1.6rem' : '2rem', fontWeight: 'bold', color: '#00D4AA' }}>{stat.number}</div>
              <div style={{ color: '#8899aa', fontSize: '0.8rem', marginTop: '0.2rem' }}>{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── PROBLEM SECTION ── */}
      <div style={{ padding: isMobile ? '3rem 1.5rem' : '5rem 2rem', maxWidth: '900px', margin: '0 auto' }}>
        <h2 style={{ fontSize: isMobile ? '1.6rem' : '2.2rem', fontWeight: 'bold', marginBottom: '1rem', textAlign: 'center' }}>
          Why talented candidates don't get the offer
        </h2>
        <p style={{ color: '#8899aa', textAlign: 'center', marginBottom: '3rem', fontSize: '1rem', lineHeight: 1.7 }}>
          It's rarely about talent. It's almost always about preparation.
        </p>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
          {[
            { icon: '❌', title: 'Resume never seen', desc: 'ATS systems filter out 85% of resumes automatically. If your resume doesn\'t match the right keywords, no human ever sees it.' },
            { icon: '❌', title: 'Generic interview prep', desc: 'Practicing random questions doesn\'t prepare you for the specific format your target company uses — be it MBB, Big4, Flipkart, Zomato or Citi. Every company is different.' },
            { icon: '❌', title: 'Expensive coaching', desc: 'A single coaching session costs ₹3,000–10,000. Most candidates can\'t afford the depth of preparation they need.' },
          ].map(item => (
            <div key={item.title} style={{ background: '#141B2D', borderRadius: '12px', padding: '1.5rem', width: isMobile ? '100%' : '260px', border: '1px solid #1a2640' }}>
              <div style={{ fontSize: '1.5rem', marginBottom: '0.75rem' }}>{item.icon}</div>
              <h3 style={{ color: '#ff6b6b', marginBottom: '0.5rem', fontSize: '1rem' }}>{item.title}</h3>
              <p style={{ color: '#8899aa', fontSize: '0.875rem', lineHeight: 1.6, margin: 0 }}>{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── SOLUTION SECTION ── */}
      <div style={{ background: '#141B2D', borderTop: '1px solid #1a2640', borderBottom: '1px solid #1a2640', padding: isMobile ? '3rem 1.5rem' : '5rem 2rem' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: isMobile ? '1.6rem' : '2.2rem', fontWeight: 'bold', marginBottom: '1rem' }}>
            CrackPrep fixes all three — <span style={{ color: '#00D4AA' }}>in one analysis.</span>
          </h2>
          <p style={{ color: '#8899aa', marginBottom: '3rem', fontSize: '1rem', lineHeight: 1.7 }}>
            Upload your resume and paste any job description. In under 5 minutes you get everything you need.
          </p>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
            {[
              {
                icon: '📊', color: '#00D4AA',
                title: 'ATS Score + Missing Keywords',
                desc: 'Know exactly where you stand. See your score breakdown and the exact keywords you\'re missing — so you can fix them before applying.',
              },
              {
                icon: '📝', color: '#5b8cff',
                title: 'Resume Rewritten for the Role',
                desc: 'Every weak section identified. Current text shown. Improved version written. Reason explained. Copy and paste into your resume.',
              },
              {
                icon: '🎯', color: '#f5a623',
                title: 'Company-Specific Interview Prep',
                desc: 'Consulting? Get BCG-style case studies. PM role? Get Flipkart product cases. Engineering? Get system design at their scale. 20+ questions with model answers.',
              },
            ].map(item => (
              <div key={item.title} style={{ background: '#0A0F1E', borderRadius: '12px', padding: '1.75rem', width: isMobile ? '100%' : '260px', border: '1px solid #1a2640', textAlign: 'left' }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>{item.icon}</div>
                <h3 style={{ color: item.color, marginBottom: '0.5rem', fontSize: '1rem', lineHeight: 1.4 }}>{item.title}</h3>
                <p style={{ color: '#8899aa', fontSize: '0.875rem', lineHeight: 1.6, margin: 0 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── HOW IT WORKS ── */}
      <div style={{ padding: isMobile ? '3rem 1.5rem' : '5rem 2rem', textAlign: 'center' }}>
        <h2 style={{ fontSize: isMobile ? '1.6rem' : '2.2rem', fontWeight: 'bold', marginBottom: '0.75rem' }}>
          Ready in 3 steps
        </h2>
        <p style={{ color: '#8899aa', marginBottom: '3rem', fontSize: '0.95rem' }}>No complicated setup. No learning curve.</p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: isMobile ? '1rem' : '1.5rem', flexWrap: 'wrap', maxWidth: '900px', margin: '0 auto', alignItems: 'flex-start' }}>
          {[
            { step: '1', title: 'Upload your resume',  desc: 'PDF or DOCX. Any format, any industry.' },
            { step: '2', title: 'Add your target role', desc: 'Company name, job title, and paste the JD.' },
            { step: '3', title: 'Get your full report', desc: 'ATS score, resume fixes, 20+ interview questions.' },
          ].map((item, idx) => (
            <div key={item.step} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: isMobile ? '100%' : '220px', position: 'relative' }}>
              {idx < 2 && !isMobile && (
                <div style={{ position: 'absolute', top: '20px', left: 'calc(100% - 10px)', width: '60px', borderTop: '2px dashed #1a2640', zIndex: 1 }} />
              )}
              <div style={{ width: '44px', height: '44px', background: '#00D4AA', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0A0F1E', fontWeight: 'bold', fontSize: '1.2rem', marginBottom: '1rem', flexShrink: 0 }}>
                {item.step}
              </div>
              <div style={{ background: '#141B2D', borderRadius: '10px', padding: '1.25rem', border: '1px solid #1a2640', width: '100%', boxSizing: 'border-box' }}>
                <h3 style={{ marginBottom: '0.4rem', fontSize: '0.95rem' }}>{item.title}</h3>
                <p style={{ color: '#8899aa', fontSize: '0.85rem', margin: 0 }}>{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: '2.5rem' }}>
          <button onClick={handleAnalyzeClick}
            style={{ background: '#00D4AA', color: '#0A0F1E', border: 'none', padding: '0.875rem 2.5rem', borderRadius: '8px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer', width: isMobile ? '100%' : 'auto' }}>
            Try It Free →
          </button>
        </div>
      </div>

      {/* ── TESTIMONIALS ── */}
      <div style={{ background: '#141B2D', borderTop: '1px solid #1a2640', borderBottom: '1px solid #1a2640', padding: isMobile ? '3rem 1.5rem' : '5rem 2rem' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <h2 style={{ fontSize: isMobile ? '1.6rem' : '2.2rem', fontWeight: 'bold', marginBottom: '0.5rem', textAlign: 'center' }}>
            Real results from real candidates
          </h2>
          <p style={{ color: '#8899aa', textAlign: 'center', marginBottom: '3rem', fontSize: '0.95rem' }}>
            Based on sample user outcomes. Individual results may vary.
          </p>
          <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', justifyContent: 'center' }}>
            {testimonials.map((t, i) => (
              <div key={i} style={{ background: '#0A0F1E', borderRadius: '12px', padding: '1.5rem', width: isMobile ? '100%' : '290px', border: '1px solid #1a2640', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '0.95rem' }}>{t.name}</div>
                    <div style={{ color: '#00D4AA', fontSize: '0.8rem', marginTop: '0.2rem' }}>{t.role}</div>
                    <div style={{ color: '#8899aa', fontSize: '0.75rem' }}>{t.prev}</div>
                  </div>
                  <div style={{ textAlign: 'center', flexShrink: 0 }}>
                    <div style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#00D4AA' }}>{t.score}</div>
                    <div style={{ fontSize: '0.6rem', color: '#8899aa' }}>ATS Score</div>
                  </div>
                </div>
                <p style={{ color: '#8899aa', fontSize: '0.85rem', lineHeight: 1.7, margin: 0, fontStyle: 'italic' }}>
                  "{t.text}"
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── COMPARISON TABLE ── */}
      <div style={{ padding: isMobile ? '3rem 1.5rem' : '5rem 2rem', maxWidth: '800px', margin: '0 auto' }}>
        <h2 style={{ fontSize: isMobile ? '1.6rem' : '2.2rem', fontWeight: 'bold', marginBottom: '0.5rem', textAlign: 'center' }}>
          Why CrackPrep beats the alternatives
        </h2>
        <p style={{ color: '#8899aa', textAlign: 'center', marginBottom: '2.5rem', fontSize: '0.95rem' }}>
          Compared to coaching classes and generic ATS tools
        </p>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
            <thead>
              <tr>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#8899aa', borderBottom: '1px solid #1a2640', fontWeight: 'normal' }}>Feature</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'center', color: '#00D4AA', borderBottom: '1px solid #1a2640', background: '#0d2018' }}>CrackPrep</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'center', color: '#8899aa', borderBottom: '1px solid #1a2640' }}>Generic ATS Tool</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'center', color: '#8899aa', borderBottom: '1px solid #1a2640' }}>Coaching Class</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['ATS Score', '✅', '✅', '❌'],
                ['Resume improvements', '✅ Tailored to JD', '⚠️ Generic tips', '✅ Manual'],
                ['Interview prep', '✅ Company-specific', '❌', '✅ Generic'],
                ['Case studies (consulting)', '✅ Top-tier style', '❌', '✅ Manual'],
                ['Ready in minutes', '✅ Under 5 mins', '✅', '❌ Weeks'],
                ['Cost per session', '✅ ₹99–₹699', '⚠️ $20+/month', '❌ ₹3,000–10,000'],
                ['Available 24/7', '✅', '✅', '❌'],
              ].map(([feature, cp, ats, coaching], i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : '#141B2D' }}>
                  <td style={{ padding: '0.75rem 1rem', color: '#8899aa' }}>{feature}</td>
                  <td style={{ padding: '0.75rem 1rem', textAlign: 'center', color: '#00D4AA', background: i % 2 === 0 ? 'rgba(0,212,170,0.03)' : 'rgba(0,212,170,0.06)' }}>{cp}</td>
                  <td style={{ padding: '0.75rem 1rem', textAlign: 'center', color: '#8899aa' }}>{ats}</td>
                  <td style={{ padding: '0.75rem 1rem', textAlign: 'center', color: '#8899aa' }}>{coaching}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── PRICING ── */}
      <div style={{ background: '#141B2D', borderTop: '1px solid #1a2640', borderBottom: '1px solid #1a2640', padding: isMobile ? '3rem 1.5rem' : '5rem 2rem', textAlign: 'center' }}>
        <h2 style={{ fontSize: isMobile ? '1.6rem' : '2.2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>Simple Pricing</h2>
        <p style={{ color: '#8899aa', marginBottom: '0.75rem', fontSize: '0.95rem' }}>
          All plans are one-time payments — no subscriptions, no hidden fees
        </p>
        <p style={{ color: '#00D4AA', fontSize: '0.9rem', marginBottom: '2.5rem', fontWeight: 'bold' }}>
          🎁 Refer a friend — both get 1 bonus analysis when they purchase
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: isMobile ? '0.75rem' : '1rem', flexWrap: 'wrap', maxWidth: '1100px', margin: '0 auto' }}>
          {plans.map(plan => (
            <div key={plan.name}
              style={{ background: '#0A0F1E', border: '2px solid ' + plan.color, borderRadius: '12px', padding: isMobile ? '1.25rem 1rem' : '1.5rem', width: isMobile ? 'calc(50% - 0.75rem)' : '165px', position: 'relative', textAlign: 'left', boxSizing: 'border-box' }}>
              {plan.tag && (
                <div style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', background: '#00D4AA', color: '#0A0F1E', fontSize: '0.6rem', fontWeight: 'bold', padding: '0.2rem 0.6rem', borderRadius: '20px', whiteSpace: 'nowrap' }}>
                  {plan.tag}
                </div>
              )}
              <div style={{ color: '#00D4AA', fontWeight: 'bold', marginBottom: '0.2rem', fontSize: '0.85rem' }}>{plan.name}</div>
              <div style={{ fontSize: isMobile ? '1.6rem' : '2rem', fontWeight: 'bold', marginBottom: '0.2rem' }}>{plan.price}</div>
              <div style={{ color: '#8899aa', fontSize: '0.75rem', marginBottom: '0.75rem' }}>{plan.desc}</div>
              <div style={{ borderTop: '1px solid #1a2640', paddingTop: '0.6rem' }}>
                {plan.features.map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.3rem', marginBottom: '0.3rem' }}>
                    <span style={{ color: '#00D4AA', fontSize: '0.75rem', marginTop: '1px' }}>✓</span>
                    <span style={{ color: '#8899aa', fontSize: '0.75rem', lineHeight: 1.4 }}>{f}</span>
                  </div>
                ))}
              </div>
              {plan.planType && (
                <button onClick={() => navigate('/pricing')}
                  style={{ marginTop: '0.75rem', width: '100%', background: plan.name === 'Elite' ? '#00D4AA' : 'transparent', color: plan.name === 'Elite' ? '#0A0F1E' : '#00D4AA', border: '1px solid #00D4AA', padding: '0.4rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem' }}>
                  Get Started
                </button>
              )}
            </div>
          ))}
        </div>
        <div style={{ marginTop: '1.5rem' }}>
          <button onClick={() => navigate('/pricing')}
            style={{ background: 'transparent', color: '#00D4AA', border: '1px solid #00D4AA', padding: '0.75rem 2rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.9rem', width: isMobile ? '100%' : 'auto' }}>
            View All Plans & Buy →
          </button>
        </div>
      </div>

      {/* ── FAQ ── */}
      <div style={{ padding: isMobile ? '3rem 1.5rem' : '5rem 2rem', maxWidth: '700px', margin: '0 auto' }}>
        <h2 style={{ fontSize: isMobile ? '1.6rem' : '2.2rem', fontWeight: 'bold', marginBottom: '0.5rem', textAlign: 'center' }}>
          Frequently asked questions
        </h2>
        <p style={{ color: '#8899aa', textAlign: 'center', marginBottom: '2.5rem', fontSize: '0.95rem' }}>
          Everything you need to know before trying CrackPrep.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {faqs.map((faq, i) => (
            <div key={i}
              style={{ background: '#141B2D', borderRadius: '10px', border: '1px solid ' + (openFaq === i ? '#00D4AA' : '#1a2640'), overflow: 'hidden', cursor: 'pointer' }}
              onClick={() => setOpenFaq(openFaq === i ? null : i)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem' }}>
                <span style={{ fontWeight: 'bold', fontSize: '0.9rem', color: openFaq === i ? '#00D4AA' : 'white', lineHeight: 1.4, paddingRight: '1rem' }}>{faq.q}</span>
                <span style={{ color: '#00D4AA', fontSize: '1.2rem', flexShrink: 0 }}>{openFaq === i ? '−' : '+'}</span>
              </div>
              {openFaq === i && (
                <div style={{ padding: '0 1.25rem 1rem', color: '#8899aa', fontSize: '0.875rem', lineHeight: 1.7 }}>
                  {faq.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── FINAL CTA ── */}
      <div style={{ background: '#141B2D', borderTop: '1px solid #1a2640', padding: isMobile ? '3rem 1.5rem' : '5rem 2rem', textAlign: 'center' }}>
        <h2 style={{ fontSize: isMobile ? '1.8rem' : '2.5rem', fontWeight: 'bold', marginBottom: '1rem', lineHeight: 1.2 }}>
          Stop leaving interviews to chance.<br />
          <span style={{ color: '#00D4AA' }}>Walk in prepared.</span>
        </h2>
        <p style={{ color: '#8899aa', marginBottom: '2rem', fontSize: '1rem', lineHeight: 1.7 }}>
          Your first analysis is completely free. ATS score, resume fixes, and 20+ interview questions — ready in under 5 minutes.
        </p>
        <button onClick={handleAnalyzeClick}
          style={{ background: '#00D4AA', color: '#0A0F1E', border: 'none', padding: isMobile ? '1rem 2rem' : '1.1rem 3rem', borderRadius: '8px', fontSize: isMobile ? '1rem' : '1.15rem', fontWeight: 'bold', cursor: 'pointer', width: isMobile ? '100%' : 'auto', boxShadow: '0 0 30px rgba(0,212,170,0.3)' }}>
          Analyze My Resume Free →
        </button>
        <p style={{ color: '#8899aa', fontSize: '0.8rem', marginTop: '0.75rem' }}>
          Trusted by candidates targeting MBB · Big4 · Accenture · Google · Flipkart · Goldman Sachs
        </p>
      </div>

      {/* ── FOOTER ── */}
      <div style={{ borderTop: '1px solid #1a2640', padding: '2rem 1.5rem', textAlign: 'center' }}>
        <span style={{ color: '#00D4AA', fontWeight: 'bold', fontSize: '1.1rem' }}>CrackPrep</span>
        <p style={{ color: '#8899aa', fontSize: '0.8rem', marginTop: '0.4rem', marginBottom: '1rem' }}>
          AI-powered resume and interview preparation for the Indian job market
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '1.25rem', flexWrap: 'wrap', fontSize: '0.8rem' }}>
          {[
            { label: 'Pricing',  path: '/pricing' },
            { label: 'About Us', path: '/about' },
            { label: 'Contact',  path: '/contact' },
            { label: 'Terms',    path: '/terms' },
            { label: 'Privacy',  path: '/privacy' },
          ].map(link => (
            <button key={link.path} onClick={() => navigate(link.path)}
              style={{ background: 'transparent', border: 'none', color: '#8899aa', cursor: 'pointer', fontSize: '0.8rem' }}>
              {link.label}
            </button>
          ))}
        </div>
        <p style={{ color: '#8899aa', fontSize: '0.75rem', marginTop: '1rem' }}>
          © 2026 CrackPrep · Built by IIT & ISB alumni
        </p>
      </div>

    </div>
  );
}